import type { CurrencyCode } from '../types';

/**
 * Money is an integer number of MINOR units everywhere in this app — paise for
 * INR, cents for USD. Nothing stores a float or a decimal string, because
 * 0.1 + 0.2 !== 0.3 and a ledger cannot afford that.
 *
 * Both supported currencies have 100 minor units, which is why the splitting
 * engine never needs to know which currency it is working in. Adding a
 * zero-decimal currency (JPY) or a three-decimal one (KWD) would mean threading
 * the exponent below through `allocate` and every parse/format site.
 */
export interface CurrencyInfo {
  code: CurrencyCode;
  symbol: string;
  locale: string;
  label: string;
  exponent: 2;
}

export const CURRENCIES: Record<CurrencyCode, CurrencyInfo> = {
  INR: { code: 'INR', symbol: '₹', locale: 'en-IN', label: 'Indian rupee', exponent: 2 },
  USD: { code: 'USD', symbol: '$', locale: 'en-US', label: 'US dollar', exponent: 2 }
};

export const CURRENCY_CODES = Object.keys(CURRENCIES) as CurrencyCode[];

export const curOf = (c?: CurrencyCode | null): CurrencyInfo =>
  (c && CURRENCIES[c]) || CURRENCIES.INR;

/** Display form. Whole amounts drop the decimals; anything else keeps both. */
export function money(minor: number, currency?: CurrencyCode | null): string {
  const c = curOf(currency);
  const neg = minor < 0;
  const n = Math.abs(Math.round(minor));
  const dec = n % 100 === 0 ? 0 : 2;
  const s = new Intl.NumberFormat(c.locale, {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec
  }).format(n / 100);
  return (neg ? '−' : '') + c.symbol + s;
}

/** Always two decimals — used by exports, where consistency beats brevity. */
export function moneyExact(minor: number, currency?: CurrencyCode | null): string {
  const c = curOf(currency);
  const neg = minor < 0;
  const n = Math.abs(Math.round(minor));
  const s = new Intl.NumberFormat(c.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n / 100);
  return (neg ? '-' : '') + c.symbol + s;
}

/** Unformatted decimal, for CSV cells that a spreadsheet must be able to sum. */
export const plain = (minor: number): string => (minor / 100).toFixed(2);

/** Parse whatever the user typed into integer minor units. */
export function toMinor(v: string | number): number {
  if (typeof v === 'number') return Math.round(v * 100);
  const cleaned = String(v).replace(/[^0-9.]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/** The inverse of toMinor, for pre-filling a field: "6300", or "999.98". */
export const toInput = (minor: number): string =>
  minor % 100 === 0 ? String(minor / 100) : (minor / 100).toFixed(2);

/** "2½" rather than "2.5" — reads better in a UI about people. */
export function wfmt(w: number): string {
  if (w === 0) return '0';
  const whole = Math.floor(w);
  const half = w - whole >= 0.5;
  if (whole === 0) return '½';
  return String(whole) + (half ? '½' : '');
}

export const sharesLabel = (w: number): string =>
  `${wfmt(w)} ${w === 1 ? 'share' : 'shares'}`;

/**
 * Local calendar date as YYYY-MM-DD.
 * `toISOString()` is UTC and would file an expense added at 10pm in India
 * under tomorrow's date.
 */
export function todayISO(d: Date = new Date()): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
}

export function formatDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}
